import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { X, Save, Loader2 } from 'lucide-react';
import { API } from '../App';

/**
 * Admin-facing modal to edit any editable field on a member profile.
 * Backend: PATCH /api/admin/members/{user_id}
 *
 * Every change is written to db.admin_audit_log server-side so we have a
 * forensic trail. Email is editable but the backend enforces uniqueness.
 * handicap_index is clamped server-side to the WHS range [-10, 54].
 *
 * onSaved receives the fresh member doc so the caller can splice it into
 * its local state without a full refetch.
 */
export default function EditMemberModal({ member, open, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!member) return;
    const gp = member.golfer_profile || {};
    setForm({
      name: member.name || '',
      email: member.email || '',
      phone: member.phone || '',
      picture: member.picture || '',
      bio: member.bio || '',
      handicap_index: gp.handicap_index ?? member.handicap_index ?? '',
      home_course: gp.home_course || member.home_course || '',
      favorite_tees: gp.favorite_tees || '',
      city: gp.city || '',
      state: gp.state || '',
    });
  }, [member]);

  if (!open || !member) return null;

  const field = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // Send everything — the backend already dedupes and only writes changed keys.
      const payload = { ...form };
      if (payload.handicap_index === '' || payload.handicap_index == null) payload.handicap_index = null;
      else payload.handicap_index = Number(payload.handicap_index);
      const res = await fetch(`${API}/admin/members/${member.user_id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (!data.changed) {
        toast.info('No changes to save');
      } else {
        toast.success(`Updated ${data.changed_fields.length} field${data.changed_fields.length !== 1 ? 's' : ''}`);
        if (onSaved) onSaved(data.member);
      }
      onClose();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9998] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-member-modal">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-bundle-cream-dark bg-bundle-green text-white">
          <div>
            <h2 className="font-heading font-black text-lg leading-tight">Edit Member</h2>
            <p className="text-white/70 text-xs mt-0.5">{member.name} · {member.email}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center"
            aria-label="Close"
            data-testid="edit-member-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <Section title="Basics">
            <Field label="Name" value={form.name} onChange={field('name')} testId="edit-name" />
            <Field label="Email" value={form.email} onChange={field('email')} type="email" testId="edit-email"
                   hint="⚠️ Changing this will change the member's login." />
            <Field label="Phone" value={form.phone} onChange={field('phone')} testId="edit-phone" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="City" value={form.city} onChange={field('city')} testId="edit-city" />
              <Field label="State" value={form.state} onChange={field('state')} testId="edit-state" />
            </div>
            <Field label="Photo URL" value={form.picture} onChange={field('picture')} testId="edit-picture"
                   hint="Paste a public image URL. Leave blank to remove." />
            <TextareaField label="Bio" value={form.bio} onChange={field('bio')} testId="edit-bio" rows={3} />
          </Section>

          <Section title="Golf">
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Handicap Index"
                value={form.handicap_index}
                onChange={field('handicap_index')}
                type="number"
                step="0.1"
                testId="edit-handicap"
                hint="WHS range: -10 to 54"
              />
              <Field label="Favorite Tees" value={form.favorite_tees} onChange={field('favorite_tees')} testId="edit-tees"
                     hint="e.g. Blue, White, Gold" />
            </div>
            <Field label="Home Course" value={form.home_course} onChange={field('home_course')} testId="edit-home-course" />
          </Section>

          <div className="pt-2 text-[11px] text-gray-400 leading-relaxed">
            Every change is written to the admin audit log with the field, old value, new value, and timestamp.
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-bundle-cream-dark bg-bundle-cream/40">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-full text-sm font-semibold text-gray-500 hover:bg-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="edit-member-save"
            className="px-5 py-2 rounded-full text-sm font-bold text-white bg-bundle-green hover:bg-bundle-green-dark shadow-sm disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-[10px] font-black uppercase tracking-widest text-bundle-green mb-2">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', hint, testId, step }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-gray-600 mb-1">{label}</span>
      <input
        type={type}
        step={step}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full px-3 py-2 border border-bundle-cream-dark rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-bundle-green/40 focus:border-bundle-green"
      />
      {hint && <span className="block text-[11px] text-gray-400 mt-1">{hint}</span>}
    </label>
  );
}

function TextareaField({ label, value, onChange, rows = 3, testId }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-gray-600 mb-1">{label}</span>
      <textarea
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        data-testid={testId}
        className="w-full px-3 py-2 border border-bundle-cream-dark rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-bundle-green/40 focus:border-bundle-green resize-none"
      />
    </label>
  );
}
